import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { ChoiceCard, Field, SelectField, StepAlert, ListSkeleton, useInstallTheme } from "../ui";
import {
  entityDisplayName,
  isIndividualType,
  lookupEntityType,
  newCustomerDefaults,
  statusLabel,
} from "../../claim/customerIdentity";

export function CustomerModeStep({ state, dispatch, onNext }) {
  const { t } = useInstallTheme();
  const choose = (mode) => {
    dispatch({ type: "SET_CUSTOMER_MODE", mode });
    setTimeout(onNext, 0);
  };
  return (
    <>
      <ChoiceCard
        title="Existing customer"
        description="Add this monitor to a customer already in Rivio."
        selected={state.branch.customerMode === "existing"}
        onClick={() => choose("existing")}
        t={t}
      />
      <ChoiceCard
        title="New customer"
        description="Create the customer, site, and tank."
        selected={state.branch.customerMode === "new"}
        onClick={() => choose("new")}
        t={t}
      />
    </>
  );
}

function entityName(entity) {
  return entityDisplayName(entity) || "Customer";
}

function entityId(entity) {
  return entity?.attributes?.entity_id ?? entity?.entity_id ?? null;
}

function entitySub(entity) {
  const a = entity?.attributes || entity || {};
  return [
    entityId(entity) != null ? `ID ${entityId(entity)}` : "",
    a?.EntityType?.type_name,
    a?.Company?.name || a?.Company?.Organization?.name,
    a?.StatusType?.status_name,
  ]
    .filter(Boolean)
    .join(" · ");
}

function searchText(entity) {
  const a = entity?.attributes || entity || {};
  const contacts = (a.contacts || a.Contacts || []).flatMap((c) => [
    c.first_name,
    c.last_name,
    ...(c.ContactAddresses || c.addresses || []).map((addr) => addr.contact_address || addr.value),
  ]);
  return [entityName(entity), entitySub(entity), a.first_name, a.last_name, ...contacts]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function CustomerSearchStep({ customers, loading, selected, onSelect, profile, t, darkMode }) {
  const { t: theme } = useInstallTheme();
  const tokens = t || theme;
  const [query, setQuery] = useState("");

  const results = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^#/, "");
    const rows = customers || [];
    if (!q) return rows.slice(0, 20);
    return rows
      .filter((e) => String(entityId(e)) === q || searchText(e).includes(q))
      .slice(0, 20);
  }, [customers, query]);

  return (
    <>
      <Field
        label="Search by name, customer ID, or contact"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        t={tokens}
        darkMode={darkMode}
        inputProps={{ "aria-label": "Search customers" }}
      />
      {selected ? <CustomerProfileCard profile={profile} entity={selected} t={tokens} /> : null}
      {loading ? (
        <ListSkeleton rows={3} t={tokens} />
      ) : (
        <Box mt={2}>
          {results.length === 0 ? (
            <StepAlert severity="info">
              No match? Choose “New customer” on the previous screen instead.
            </StepAlert>
          ) : (
            results.map((entity, index) => {
              const id = entityId(entity) ?? index;
              const isSelected = selected && String(entityId(selected)) === String(id);
              return (
                <ChoiceCard
                  key={id}
                  title={entityName(entity)}
                  description={entitySub(entity)}
                  selected={isSelected}
                  onClick={() => onSelect(entity)}
                  t={tokens}
                />
              );
            })
          )}
        </Box>
      )}
    </>
  );
}

function addressText(row) {
  return row?.formatted_address || [row?.street_address1, row?.city, row?.state, row?.postal_code].filter(Boolean).join(", ");
}

// Read-only profile of an existing customer, from the scoped claim-setup lookup.
export function CustomerProfileCard({ profile, entity, t }) {
  if (!profile || profile.loading) {
    return (
      <Box mt={2} p={2} borderRadius="16px" sx={{ background: t.softSurface, border: `1px solid ${t.borderSubtle}` }}>
        <Typography variant="body2">Loading customer profile…</Typography>
      </Box>
    );
  }
  if (profile.error) {
    return <StepAlert severity="warning">Customer profile could not be loaded: {profile.error}</StepAlert>;
  }
  const customer = profile.customer || {};
  const contacts = (profile.recipients || []).filter((row) => row.scope === "entity");
  const primary = contacts.find((row) => row.is_primary) || contacts[0];
  const addresses = profile.addresses || [];
  const assets = profile.assets || [];
  return (
    <Box mt={2} p={2} borderRadius="16px" sx={{ background: t.softSurfaceStrong, border: `1px solid ${t.borderSubtle}` }}>
      <Typography fontWeight={700}>{customer.display_name || entityName(entity)}</Typography>
      <Typography variant="body2" sx={{ color: t.textMuted }}>
        {[`Customer ID ${customer.entity_id ?? entityId(entity)}`, customer.entity_type, customer.status, customer.company].filter(Boolean).join(" · ")}
      </Typography>
      <Typography variant="body2" mt={1}>
        <strong>Primary contact:</strong>{" "}
        {primary
          ? `${primary.contact_name}${primary.contact_type ? ` (${primary.contact_type})` : ""} ${(primary.addresses || []).map((a) => a.value).join(" · ")}`
          : "None on file"}
      </Typography>
      <Typography variant="body2" mt={0.5}>
        <strong>Service addresses:</strong>{" "}
        {addresses.length ? addresses.map((a) => [a.name, addressText(a)].filter(Boolean).join(" - ")).join("; ") : "None on file"}
      </Typography>
      <Typography variant="body2" mt={0.5}>
        <strong>Assets:</strong>{" "}
        {assets.length ? assets.map((a) => a.name || a.map_identifier || a.item_name || `Asset #${a.address_item_id}`).join(", ") : "None on file"}
      </Typography>
      {!primary || !(primary.addresses || []).length ? (
        <StepAlert severity="warning">This customer has no contact phone or email on file.</StepAlert>
      ) : null}
      {!addresses.length ? <StepAlert severity="warning">This customer has no service address on file.</StepAlert> : null}
      <Typography variant="caption" sx={{ color: t.textMuted }}>
        Customer details are read-only during installation.
      </Typography>
    </Box>
  );
}

export function CustomerCreateStep({ form, dropdowns, dispatch, t, darkMode }) {
  const tokens = t;
  const entityType = lookupEntityType(dropdowns, form.entity_type_id);
  const isIndividual = isIndividualType(entityType);
  const defaults = newCustomerDefaults(dropdowns);
  const set = (patch) => dispatch({ type: "SET_CUSTOMER_FORM", form: patch });
  const statuses = dropdowns.statusOptions || dropdowns.statusTypes || [];
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {defaults.errors.map((message) => (
        <StepAlert key={message} severity="warning">
          {message}
        </StepAlert>
      ))}
      <SelectField
        label="Customer type"
        value={form.entity_type_id}
        onChange={(e) => set({ entity_type_id: e.target.value })}
        t={tokens}
        darkMode={darkMode}
        required
      >
        {(dropdowns.entityTypes || []).map((row) => (
          <MenuItem key={row.entity_type_id} value={String(row.entity_type_id)}>
            {row.type_name || row.name}
          </MenuItem>
        ))}
      </SelectField>
      <SelectField
        label="Company"
        value={form.company_id}
        onChange={(e) => set({ company_id: e.target.value })}
        t={tokens}
        darkMode={darkMode}
        helperText={defaults.company_id && String(defaults.company_id) === String(form.company_id) ? "Defaulted to Rivio" : undefined}
        required
      >
        {(dropdowns.companies || dropdowns.company || []).map((row) => (
          <MenuItem key={row.company_id ?? row.id} value={String(row.company_id ?? row.id)}>
            {row.name || row.Organization?.name}
          </MenuItem>
        ))}
      </SelectField>
      <SelectField
        label="Status"
        value={form.status_type_id}
        onChange={(e) => set({ status_type_id: e.target.value })}
        t={tokens}
        darkMode={darkMode}
        helperText={defaults.status_type_id && String(defaults.status_type_id) === String(form.status_type_id) ? "Defaulted to Active" : undefined}
        required
      >
        {statuses.map((row) => (
          <MenuItem key={row.status_option_id ?? row.status_type_id} value={String(row.status_type_id)}>
            {statusLabel(row)}
          </MenuItem>
        ))}
      </SelectField>
      {isIndividual ? (
        <>
          <Field
            label="First name"
            value={form.first_name}
            onChange={(e) => set({ first_name: e.target.value })}
            t={tokens}
            darkMode={darkMode}
            required
          />
          <Field
            label="Last name"
            value={form.last_name}
            onChange={(e) => set({ last_name: e.target.value })}
            t={tokens}
            darkMode={darkMode}
            required
          />
        </>
      ) : (
        <Field
          label="Customer name"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
          t={tokens}
          darkMode={darkMode}
          required
        />
      )}
    </Box>
  );
}
